/**
 * Custom hooks per Workflows
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import { toast } from "@/lib/toast-service";
import type {
    WorkflowCreate,
    WorkflowListFilters,
    WorkflowResponse,
    WorkflowUpdate,
    WorkflowCompanyResponse,
    WorkflowExecutionLogResponse,
    DeleteResponse,
} from "@/types/api/workflows";

const WORKFLOWS_QUERY_KEYS = {
    all: ["workflows"] as const,
    lists: () => [...WORKFLOWS_QUERY_KEYS.all, "list"] as const,
    list: (filters?: WorkflowListFilters) => [...WORKFLOWS_QUERY_KEYS.lists(), filters] as const,
    details: () => [...WORKFLOWS_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...WORKFLOWS_QUERY_KEYS.details(), id] as const,
    companies: (workflowId: string) => [...WORKFLOWS_QUERY_KEYS.detail(workflowId), "companies"] as const,
    executions: (workflowId: string) => [...WORKFLOWS_QUERY_KEYS.detail(workflowId), "executions"] as const,
};

/**
 * Hook per lista workflows con filtri
 */
export function useWorkflows(
    filters?: WorkflowListFilters,
    options?: Omit<UseQueryOptions<WorkflowResponse[], Error>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: WORKFLOWS_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<WorkflowResponse[]> => {
            return await apiGet<WorkflowResponse[]>("/workflows", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook per singolo workflow
 */
export function useWorkflow(workflowId: string) {
    return useQuery({
        queryKey: WORKFLOWS_QUERY_KEYS.detail(workflowId),
        queryFn: async (): Promise<WorkflowResponse> => {
            return await apiGet<WorkflowResponse>(`/workflows/${workflowId}`);
        },
        enabled: !!workflowId && workflowId !== 'new',
    });
}

/**
 * Hook per creare workflow
 */
export function useCreateWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: WorkflowCreate): Promise<WorkflowResponse> => {
            return await apiPost<WorkflowResponse>("/workflows", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.lists() });
            toast.success("Workflow creato con successo");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante la creazione del workflow");
        },
    });
}

/**
 * Hook per aggiornare workflow
 */
export function useUpdateWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, data }: { workflowId: string; data: WorkflowUpdate }): Promise<WorkflowResponse> => {
            return await apiPut<WorkflowResponse>(`/workflows/${workflowId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.detail(variables.workflowId) });
            toast.success("Workflow aggiornato con successo");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante l'aggiornamento del workflow");
        },
    });
}

/**
 * Hook per eliminare workflow
 */
export function useDeleteWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (workflowId: string): Promise<DeleteResponse> => {
            return await apiDelete<DeleteResponse>(`/workflows/${workflowId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.lists() });
            toast.success("Workflow eliminato con successo");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante l'eliminazione del workflow");
        },
    });
}

/**
 * Hook per lista companies associate a un workflow
 */
export function useWorkflowCompanies(workflowId: string) {
    return useQuery({
        queryKey: WORKFLOWS_QUERY_KEYS.companies(workflowId),
        queryFn: async (): Promise<WorkflowCompanyResponse[]> => {
            return await apiGet<WorkflowCompanyResponse[]>(`/workflows/${workflowId}/companies`);
        },
        enabled: !!workflowId,
    });
}

/**
 * Hook per associare company a workflow
 */
export function useAssociateCompanyToWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, companyId }: { workflowId: string; companyId: string }): Promise<WorkflowCompanyResponse> => {
            return await apiPost<WorkflowCompanyResponse>(`/workflows/${workflowId}/companies?company_id=${companyId}`);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.companies(variables.workflowId) });
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.detail(variables.workflowId) });
            toast.success("Company associata con successo");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante l'associazione della company");
        },
    });
}

/**
 * Hook per rimuovere associazione company da workflow
 */
export function useRemoveCompanyFromWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, companyId }: { workflowId: string; companyId: string }): Promise<void> => {
            return await apiDelete<void>(`/workflows/${workflowId}/companies/${companyId}`);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.companies(variables.workflowId) });
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.detail(variables.workflowId) });
            toast.success("Associazione rimossa con successo");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante la rimozione dell'associazione");
        },
    });
}

/**
 * Hook per richiedere la rianalisi AI di un workflow
 */
export function useReanalyzeWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (workflowId: string): Promise<WorkflowResponse> => {
            return await apiPost<WorkflowResponse>(`/workflows/${workflowId}/reanalyze`);
        },
        onSuccess: (_data, workflowId) => {
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: WORKFLOWS_QUERY_KEYS.detail(workflowId) });
            toast.success("Analisi workflow avviata");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.detail || "Errore durante l'avvio dell'analisi");
        },
    });
}

/**
 * Hook per lista esecuzioni workflow
 */
export function useWorkflowExecutions(
    workflowId: string,
    filters?: { skip?: number; limit?: number; status?: string },
    options?: Omit<UseQueryOptions<WorkflowExecutionLogResponse[], Error>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: [...WORKFLOWS_QUERY_KEYS.executions(workflowId), filters],
        queryFn: async (): Promise<WorkflowExecutionLogResponse[]> => {
            return await apiGet<WorkflowExecutionLogResponse[]>(`/workflows/${workflowId}/executions`, {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        enabled: !!workflowId,
        ...options,
    });
}

